import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Stack,
} from "@mui/material";
import { useUser } from "@clerk/clerk-react";
import { useTheme, alpha } from "@mui/material/styles";
import { tokens } from "../theme";
import { setCacheItem, getCacheItem } from "../utils/cache";
import { useSleeperConnection } from "../hooks/useSleeperConnection";
import { useToast } from "../contexts/ToastContext";

const LAST_USERNAME_KEY = "sleeper-last-username";
const LEAGUES_CACHE_KEY = "sleeper-leagues";
const IMPORT_SUMMARY_KEY = "sleeper-import-summary";

export default function SleeperConnect({ onClose }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { user } = useUser();
    const { markAsConnected } = useSleeperConnection();
    const { showSuccess, showError } = useToast();
    
    const [formData, setFormData] = useState({
        username: "",
        leagueId: "",
        season: new Date().getFullYear().toString(),
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [importSummary, setImportSummary] = useState(null);
    const [leagues, setLeagues] = useState([]);
    const [step, setStep] = useState("form"); // form, success, leagues

    // Load cached data on mount
    useEffect(() => {
        const lastUsername = getCacheItem(LAST_USERNAME_KEY);
        const cachedLeagues = getCacheItem(LEAGUES_CACHE_KEY);
        const cachedSummary = getCacheItem(IMPORT_SUMMARY_KEY);
        
        if (lastUsername) {
            setFormData(prev => ({ ...prev, username: lastUsername }));
        }
        
        if (cachedLeagues) {
            // Handle both direct array and paginated response formats
            const leaguesArray = Array.isArray(cachedLeagues) ? cachedLeagues : (cachedLeagues.results || []);
            if (leaguesArray.length > 0) {
                setLeagues(leaguesArray);
                setStep("leagues");
            }
        }
        
        if (cachedSummary) {
            setImportSummary(cachedSummary);
        }
    }, []);

    const getApiHeaders = () => {
        const headers = {
            "Content-Type": "application/json",
        };
        
        if (user) {
            headers["X-Clerk-User-Id"] = user.id || "unknown";
            headers["X-Clerk-Email"] = user.primaryEmailAddress?.emailAddress || "";
            headers["X-Clerk-Username"] = user.username || "";
        }
        
        // Only use one consistent header name for the API key
        const apiKey = process.env.REACT_APP_API_SHARED_KEY;
        if (apiKey) {
            headers["X-API-KEY"] = apiKey;  // Use one consistent case
        }
        
         // Add debug logging
        console.log("[getApiHeaders] API key present and length:", !!apiKey, apiKey?.length);
        
        return headers;
    };

    const getApiBaseUrl = () => {
        // In development, the proxy handles this, so we use relative URLs
        // In production, we need to use the actual backend API URL
        if (process.env.NODE_ENV === "development") {
            console.log("[SleeperConnect] Development mode - using proxy");
            return "";
        } else {
            // Production mode - use the backend URL WITHOUT /api suffix since we add it in the endpoint construction
            // Check environment variables in order of preference, but strip any /api suffix
            let backendUrl = process.env.REACT_APP_API_URL || 
                           process.env.NEXT_PUBLIC_API_URL || 
                           process.env.REACT_APP_API_BASE_URL || 
                           process.env.API_BASE_URL || 
                           "https://weez-nfl-data.vercel.app";
            
            // Strip any trailing /api from the URL
            if (backendUrl.endsWith('/api')) {
                backendUrl = backendUrl.slice(0, -4);
            }
            
            console.log("[SleeperConnect] Production mode - using backend URL:", backendUrl);
            return backendUrl;
        }
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
        setError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!formData.username.trim()) {
            setError("Sleeper username is required");
            return;
        }
        
        setLoading(true);
        setError("");
        setSuccess("");
        
        try {
            const baseUrl = getApiBaseUrl();
            // Construct the URL properly - baseUrl should NOT include /api
            const apiUrl = `${baseUrl}/api/leagues/import-sleeper/`;
        
            const headers = getApiHeaders();
            const requestBody = {
                username: formData.username.trim(),
                season: parseInt(formData.season) || new Date().getFullYear(),
            };
            
            // Debug logging (can remove in production)
            if (process.env.NODE_ENV === "development") {
                console.log("Making Sleeper import request:", requestBody);
            }
            
            //const apiUrl = `${baseUrl}${baseUrl.endsWith('/api') ? '' : '/api'}/leagues/import-sleeper/?api_shared_key=f1b4c9e9a3d24c1f8e0b9d2a6f74c3b1e6d6a7c8b9f0a1d2a3e4f5a6b7c8d9e0`;
            console.log("[SleeperConnect] Making request to:", apiUrl);
            console.log("[SleeperConnect] Headers:", headers);
            console.log("[SleeperConnect] Request body:", requestBody);
            
            const response = await fetch(apiUrl, {
                method: "POST",
                headers,
                body: JSON.stringify(requestBody),
            });// Add this debug log

            console.log("[SleeperConnect] Full request details:", {
                url: apiUrl,
                method: "POST",
                headers: headers,
                body: requestBody,
                responseStatus: response.status,
                responseHeaders: Object.fromEntries(response.headers.entries())
            });
            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error("Sleeper user not found. Please check the username and try again.");
                }
                
                if (response.status === 502) {
                    throw new Error("Backend server error (502). The leagues endpoint may not exist or there's a server issue. Please check if the /api/leagues/ endpoint is deployed to your backend.");
                }
                
                // Try to get detailed error information
                let errorData = {};
                try {
                    errorData = await response.json();
                } catch (e) {
                    // If we can't parse JSON, try to get text
                    try {
                        const errorText = await response.text();
                        errorData = { message: errorText };
                    } catch (e2) {
                        errorData = {};
                    }
                }
                
                console.error("API Error Details:", {
                    status: response.status,
                    statusText: response.statusText,
                    headers: Object.fromEntries(response.headers.entries()),
                    errorData,
                    apiUrl,
                    requestBody
                });
                
                const errorMessage = errorData.error || errorData.message || errorData.detail || 
                    `Backend error (${response.status}): ${response.statusText}. Check browser console for details.`;
                throw new Error(errorMessage);
            }
            
            const summary = await response.json();
            setImportSummary(summary);
            
            // Cache the username for future use
            setCacheItem(LAST_USERNAME_KEY, formData.username.trim());
            setCacheItem(IMPORT_SUMMARY_KEY, summary, 5 * 60 * 1000); // 5 minutes
            
            const successMessage = `Successfully imported! ${summary.fetched || 0} fetched, ${summary.created || 0} created, ${summary.existing || 0} existing.`;
            setSuccess(successMessage);
            showSuccess(successMessage);
            
            // Fetch leagues
            await fetchLeagues();
            
            // Set default keeper settings for newly created leagues
            if (summary.created > 0) {
                await setDefaultKeeperSettings();
            }
            
        } catch (err) {
            console.error("Import error:", err);
            const errorMessage = err.message || "An unexpected error occurred. Please try again.";
            setError(errorMessage);
            showError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeagues = async () => {
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/leagues/`, {
                method: "GET",
                headers: getApiHeaders(),
            });
            
            if (!response.ok) {
                throw new Error(`Failed to fetch leagues: ${response.status}`);
            }
            
            const leaguesData = await response.json();
            
            // Handle both direct array and paginated response formats
            const leaguesArray = Array.isArray(leaguesData) ? leaguesData : (leaguesData.results || []);
            
            console.log("Leagues API response:", leaguesData);
            console.log("Extracted leagues array:", leaguesArray);
            
            setLeagues(leaguesArray);
            setCacheItem(LEAGUES_CACHE_KEY, leaguesArray, 10 * 60 * 1000); // 10 minutes
            markAsConnected(leaguesArray); // Update the global connection state
            setStep("leagues");
            
        } catch (err) {
            console.error("Fetch leagues error:", err);
            const errorMessage = "Failed to fetch leagues. " + err.message;
            setError(errorMessage);
            showError(errorMessage);
        }
    };

    const setDefaultKeeperSettings = async () => {
        try {
            const baseUrl = getApiBaseUrl();
            const leaguesResponse = await fetch(`${baseUrl}/api/leagues/`, {
                method: "GET",
                headers: getApiHeaders(),
            });
            
            if (!leaguesResponse.ok) {
                console.warn("Could not fetch leagues to set default keeper settings");
                return;
            }
            
            const leaguesData = await leaguesResponse.json();
            const leaguesArray = Array.isArray(leaguesData) ? leaguesData : (leaguesData.results || []);
            
            // Default keeper settings values as requested
            const defaultKeeperSettings = {
                keeper_max_years_kept: 2,           // 2 years an owner can keep
                keeper_highest_round: 3,            // no rounds of 3 (cannot keep rounds 1-3)
                keeper_round_cost: 2,               // 2 rounds of cost
                keeper_undrafted_round: 10          // round 10 for undrafted
            };
            
            // Set default keeper settings for each league that doesn't have them
            const updatePromises = leaguesArray.map(async (league) => {
                try {
                    // Check if the league already has keeper settings
                    const leagueResponse = await fetch(`${baseUrl}/api/leagues/${league.id}/`, {
                        method: "GET",
                        headers: getApiHeaders(),
                    });
                    
                    if (leagueResponse.ok) {
                        const leagueData = await leagueResponse.json();
                        
                        // Only set defaults if keeper settings are not already set
                        const hasKeeperSettings = leagueData.keeper_max_years_kept !== null && 
                                                leagueData.keeper_max_years_kept !== undefined;
                        
                        if (!hasKeeperSettings) {
                            console.log(`Setting default keeper settings for league ${league.id}: ${league.name}`);
                            
                            const updateResponse = await fetch(`${baseUrl}/api/leagues/${league.id}/`, {
                                method: 'PATCH',
                                headers: getApiHeaders(),
                                body: JSON.stringify(defaultKeeperSettings),
                            });
                            
                            if (!updateResponse.ok) {
                                console.warn(`Failed to set default keeper settings for league ${league.id}`);
                            } else {
                                console.log(`Successfully set default keeper settings for league ${league.id}`);
                            }
                        }
                    }
                } catch (err) {
                    console.warn(`Error setting default keeper settings for league ${league.id}:`, err);
                }
            });
            
            await Promise.allSettled(updatePromises);
            
        } catch (err) {
            console.warn("Error setting default keeper settings:", err);
        }
    };

    const handleTryAgain = () => {
        setStep("form");
        setError("");
        setSuccess("");
        setImportSummary(null);
    };

    const renderForm = () => (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Stack spacing={3}>
                <TextField
                    label="Sleeper Username"
                    value={formData.username}
                    onChange={handleInputChange("username")}
                    required
                    fullWidth
                    placeholder="Enter your Sleeper username"
                    error={!!error && !formData.username.trim()}
                    helperText={!formData.username.trim() && error ? "Username is required" : ""}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: colors.primary[500],
                            '& fieldset': {
                                borderColor: colors.grey[700],
                            },
                            '&:hover fieldset': {
                                borderColor: colors.grey[600],
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: colors.blueAccent[500],
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: colors.grey[300],
                        },
                        '& .MuiInputBase-input': {
                            color: colors.grey[100],
                        },
                        '& .MuiFormHelperText-root': {
                            color: colors.grey[400],
                        },
                    }}
                />
                
                <TextField
                    label="League ID (Optional)"
                    value={formData.leagueId}
                    onChange={handleInputChange("leagueId")}
                    fullWidth
                    placeholder="Leave blank to import all leagues"
                    helperText="Optional: Import a specific league by ID"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: colors.primary[500],
                            '& fieldset': {
                                borderColor: colors.grey[700],
                            },
                            '&:hover fieldset': {
                                borderColor: colors.grey[600],
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: colors.blueAccent[500],
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: colors.grey[300],
                        },
                        '& .MuiInputBase-input': {
                            color: colors.grey[100],
                        },
                        '& .MuiFormHelperText-root': {
                            color: colors.grey[400],
                        },
                    }}
                />
                
                <TextField
                    label="Season"
                    type="number"
                    value={formData.season}
                    onChange={handleInputChange("season")}
                    fullWidth
                    InputProps={{
                        inputProps: { min: 2020, max: new Date().getFullYear() + 1 }
                    }}
                    helperText={`Default: ${new Date().getFullYear()}`}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: colors.primary[500],
                            '& fieldset': {
                                borderColor: colors.grey[700],
                            },
                            '&:hover fieldset': {
                                borderColor: colors.grey[600],
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: colors.blueAccent[500],
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: colors.grey[300],
                        },
                        '& .MuiInputBase-input': {
                            color: colors.grey[100],
                        },
                        '& .MuiFormHelperText-root': {
                            color: colors.grey[400],
                        },
                    }}
                />
                
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            backgroundColor: colors.redAccent[800],
                            color: colors.redAccent[100],
                            '& .MuiAlert-icon': {
                                color: colors.redAccent[300],
                            },
                        }}
                    >
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            backgroundColor: colors.greenAccent[800],
                            color: colors.greenAccent[100],
                            '& .MuiAlert-icon': {
                                color: colors.greenAccent[300],
                            },
                        }}
                    >
                        {success}
                    </Alert>
                )}
                
                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button 
                        onClick={onClose} 
                        disabled={loading}
                        sx={{
                            color: colors.grey[300],
                            borderColor: colors.grey[600],
                            '&:hover': {
                                backgroundColor: colors.grey[700],
                                borderColor: colors.grey[500],
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formData.username.trim()}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                            backgroundColor: colors.greenAccent[600],
                            color: colors.grey[100],
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            '&:hover': {
                                backgroundColor: colors.greenAccent[500],
                            },
                            '&:disabled': {
                                backgroundColor: colors.grey[700],
                                color: colors.grey[500],
                            },
                        }}
                    >
                        {loading ? "Importing..." : "Import from Sleeper"}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );

    const renderLeagues = () => (
        <Box sx={{ mt: 2 }}>
            {importSummary && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Import completed! {importSummary.fetched || 0} fetched, {importSummary.created || 0} created, {importSummary.existing || 0} existing.
                </Alert>
            )}
            
            <Typography variant="h6" sx={{ mb: 2, color: colors.grey[100] }}>
                Your Sleeper Leagues
            </Typography>
            
            {leagues.length === 0 ? (
                <Alert severity="info">
                    No leagues found. Try importing again or check your Sleeper username.
                </Alert>
            ) : (
                <Stack spacing={2}>
                    {leagues.map((league, index) => (
                        <Card
                            key={league.id || index}
                            sx={{
                                backgroundColor: colors.primary[400],
                                border: `1px solid ${colors.grey[700]}`,
                                borderRadius: 2,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: colors.blueAccent[500],
                                    backgroundColor: alpha(colors.primary[400], 0.8),
                                },
                            }}
                        >
                            <CardContent sx={{ py: 2 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ color: colors.grey[100], fontWeight: 600 }}>
                                            {league.name || "Unnamed League"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                                            {league.season ? `Season: ${league.season}` : ""} 
                                            {league.total_rosters ? ` • ${league.total_rosters} teams` : ""}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={league.status || "active"}
                                        size="small"
                                        color={league.status === "complete" ? "success" : "primary"}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
            
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
                <Button onClick={handleTryAgain}>
                    Import Again
                </Button>
                <Button onClick={onClose} variant="contained">
                    Done
                </Button>
            </Box>
        </Box>
    );

    return (
        <Card
            sx={{
                backgroundColor: colors.primary[400],
                border: `1px solid ${colors.grey[700]}`,
                borderRadius: 2,
                minWidth: 450,
                maxWidth: 600,
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
            }}
        >
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ color: colors.grey[100], fontWeight: 700, mb: 1 }}>
                            Connect to Sleeper
                        </Typography>
                        <Typography variant="body1" sx={{ color: colors.grey[300], lineHeight: 1.5 }}>
                            Import your Sleeper leagues and data to get started with your fantasy football dashboard.
                        </Typography>
                    </Box>
                    <Button
                        onClick={onClose}
                        sx={{
                            minWidth: 'auto',
                            color: colors.grey[400],
                            '&:hover': {
                                backgroundColor: colors.grey[700],
                                color: colors.grey[100],
                            },
                        }}
                    >
                        ✕
                    </Button>
                </Box>
                
                {/* Debug info - remove this in production */}
                {process.env.NODE_ENV === "development" && (
                    <Alert 
                        severity="info" 
                        sx={{ 
                            mb: 3, 
                            fontSize: 12,
                            backgroundColor: colors.blueAccent[800],
                            color: colors.blueAccent[100],
                            '& .MuiAlert-icon': {
                                color: colors.blueAccent[300],
                            },
                        }}
                    >
                        <strong>Debug:</strong> User ID: {user?.id || "none"} | 
                        Email: {user?.primaryEmailAddress?.emailAddress || "none"} | 
                        Username: {user?.username || "none"}
                    </Alert>
                )}
                
                <Divider sx={{ borderColor: colors.grey[700], mb: 3 }} />
                
                {step === "form" && renderForm()}
                {step === "leagues" && renderLeagues()}
            </CardContent>
        </Card>
    );
}
